import {
  getPaymentMethodsSource,
  getProductConfig,
  type PaymentMethodsSource,
} from '@/app/config/products.config';
import type {
  CardBillingMode,
  CatalogBillingMethod,
  FinancePaymentMethod,
  PaymentMethodCatalogSource,
  PixBillingMode,
} from '@/services';

/** Ordem fixa das formas do gateway: é a ordem dos cartões e das linhas do "de/para". */
export const GATEWAY_METHOD_ORDER: FinancePaymentMethod[] = [
  'PIX',
  'CREDIT_CARD',
  'BOLETO_PIX',
];

export const CARD_MODE_LABELS: Record<CardBillingMode, string> = {
  PER_INSTALLMENT: 'Link por parcela',
  SINGLE_TRANSACTION: 'One shot (transação única)',
};

export const PIX_MODE_LABELS: Record<PixBillingMode, string> = {
  SCHEDULED: 'PIX por link',
  AUTOMATIC: 'PIX Automático',
};

/** Mesmos rótulos do BFF (`billingMethodLabel`): o histórico mostra o mesmo texto. */
export const BILLING_METHOD_LABELS: Record<CatalogBillingMethod, string> = {
  PIX: 'PIX',
  CARTAO_CREDITO: 'Cartão de crédito',
  BOLETO_PIX: 'Boleto PIX',
  TODOS_MEIOS: 'Todos os meios',
  FICHA_COMPENSACAO: 'Boleto bancário (legado)',
  DEBITO_CONTA: 'Débito em conta (legado)',
};

export const NO_GATEWAY_USE_LABEL = 'Sem uso no gateway';

/**
 * Valor do select para "sem uso no gateway". Não pode ser texto vazio: o
 * select trata vazio como "nada escolhido".
 */
export const NO_GATEWAY_USE = 'NONE';

export function billingMethodLabel(
  billingMethod?: CatalogBillingMethod
): string {
  return billingMethod
    ? BILLING_METHOD_LABELS[billingMethod]
    : NO_GATEWAY_USE_LABEL;
}

/** Opções do select "Uso no gateway". */
export const BILLING_METHOD_OPTIONS: { value: string; label: string }[] = [
  ...(Object.keys(BILLING_METHOD_LABELS) as CatalogBillingMethod[]).map(
    value => ({
      value,
      label: BILLING_METHOD_LABELS[value],
    })
  ),
  { value: NO_GATEWAY_USE, label: NO_GATEWAY_USE_LABEL },
];

export const CATALOG_SOURCE_LABELS: Record<PaymentMethodCatalogSource, string> =
  {
    INSUREMO: 'InsureMO',
    SHADOW: 'InsureMO (comparando com o DynamoDB)',
    DYNAMODB: 'DynamoDB',
  };

/**
 * O que a tela diz de um produto cujas formas de pagamento não são
 * configuradas aqui (`paymentMethodsSource` do `products.config.ts`).
 */
export const EXTERNAL_PAYMENT_SOURCE_INFO: Record<
  Exclude<PaymentMethodsSource, 'portal'>,
  { label: string; description: string }
> = {
  'calculation-engine': {
    label: 'Configurado no Motor de Cálculo',
    description:
      'As formas de pagamento deste produto vêm do i4pro, sincronizadas pelo Motor de Cálculo, e o corretor escolhe na própria cotação. A cobrança é do I4Pro: nada desta tela vale para ele.',
  },
};

/**
 * Produto cujas formas de pagamento ficam fora do Portal: o nome e o aviso que
 * a tela mostra no lugar do editor. `undefined` = o produto é editado aqui.
 */
export function describeExternalPaymentSource(
  productCode: string
): { productName: string; label: string; description: string } | undefined {
  const source = getPaymentMethodsSource(productCode);
  if (source === 'portal') return undefined;

  return {
    productName: getProductConfig(productCode)?.displayName ?? productCode,
    ...EXTERNAL_PAYMENT_SOURCE_INFO[source],
  };
}

/** O débito em conta precisa do banco: é ele que vai para o I4Pro. */
export const BILLING_METHOD_REQUIRING_BANK: CatalogBillingMethod =
  'DEBITO_CONTA';
